import { memo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { HighlightBanner, WidgetEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import { CommunityPostListDocument } from 'generated/resources/graphql'
import { FeatureFlagAvailabilitiesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useCanCreatePosts } from 'modules/dashboard/components/Groups/GroupPicker/GroupInfo'
import { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'
import { COMMUNITY_ITEM_TYPES, COMMUNITY_WIDGET_TYPES } from 'modules/dashboard/lib/events'
import CommunityPost from 'modules/posts/components/CommunityPost'
import PostInsightsModal from 'modules/posts/components/PostInsightsModal'
import { PostImpressionTrackerProvider } from 'modules/posts/hooks/usePostImpressionTracker'
import { useShowKudosCard } from 'modules/posts/hooks/useShowKudosCard'

import Skeleton from './Skeleton'
import { getQueryFiltersFromDropdown } from './helpers'
import './index.styles.css'
type CommunityPostListProps = {
  selectedCommunity: CommunitiesDropdown
  withPostBackground?: boolean
}

const CommunityPostList = ({ selectedCommunity, withPostBackground }: CommunityPostListProps) => {
  const [insightsPostId, setInsightsPostId] = useState<string | null>(null)

  const {
    data,
    loading: loadingPosts,
    controls: { fetchMore: fetchMorePosts, hasMore: hasMorePosts, loading: loadingNextPosts },
  } = usePaginatedQuery(CommunityPostListDocument, {
    variables: getQueryFiltersFromDropdown(selectedCommunity),
    extractConnection: (dataToExtract) =>
      dataToExtract.posts.postsConnection ?? {
        pageInfo: {
          hasNextPage: false,
        },
        edges: [],
      },
  })

  const posts = data?.posts.postsConnection.edges?.map((edge) => edge.node.id) || []
  const showKudosCard = useShowKudosCard({
    communitiesDropdown: selectedCommunity,
  })

  const canCreatePosts = useCanCreatePosts({ communitiesDropdown: selectedCommunity })

  const handleKudosClick = () => {
    track(trackingEvents.dashboard.clickOnKudosShareLove)
    navigate(navigation.dashboard.kudos.new.fullPath())
  }

  const kudosBannerProps = {
    title: i18n.t('posts.kudos.new_banner.title'),
    subtitle: i18n.t('posts.kudos.new_banner.subtitle'),
    buttonLabel: i18n.t('posts.kudos.new_banner.action'),
    onClick: handleKudosClick,
  }

  const getNextPage = () => {
    if (hasMorePosts) {
      fetchMorePosts()
    }
  }

  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const location = useLocation()
  const isFromCommunities = location.pathname.includes(navigation.communities.fullPath())

  const handlePostClick = (postId: string) => {
    const postIndex = data?.posts.postsConnection.edges.findIndex(
      (edge) => `${edge.node.id}` === postId
    )

    if (postIndex !== undefined && postIndex !== -1) {
      const order = postIndex + 1
      track(trackingEvents.dashboard.clickOnCommunityItem, {
        widget: COMMUNITY_WIDGET_TYPES.POST,
        type: COMMUNITY_ITEM_TYPES.REGULAR_POST,
        order,
      })
    }

    if (isFromCommunities) {
      navigate(
        navigation.communities.post.id.fullPath({
          id: postId,
        })
      )
    } else {
      navigate(
        navigation.dashboard.post.id.fullPath({
          id: postId,
        })
      )
    }
  }

  const handleCommentClick = (postId: string) => {
    handlePostClick(postId)
  }

  const handleClickCreatePost = () => {
    track(trackingEvents.dashboard.clickOnCreateNewPost)
    navigate(navigation.dashboard.post.new.fullPath())
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  const handleGroupClick = (groupId: string) => {
    // TODO
  }

  const callbacks = {
    onPostClick: handlePostClick,
    onCommentClick: handleCommentClick,
    onGroupClick: handleGroupClick,
    onInsightsClick: setInsightsPostId,
  }

  const firstPostId = posts?.[0]
  const postContainerStyles = withPostBackground
    ? { className: 'CommunityPostList_postWithBackground' }
    : { className: 'CommunityPostList_postWithoutBackground' }

  const { company } = useCurrentSession()

  const POST_PINNING_FEATURE_FLAG = 'dev_post_pinning'
  const POST_IMPRESSION_EVENTS_FEATURE_FLAG = 'dev_posts_impression_events'
  const { data: featureFlagData } = useQuery(FeatureFlagAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      companyId: company.id,
      featureNames: [POST_PINNING_FEATURE_FLAG, POST_IMPRESSION_EVENTS_FEATURE_FLAG],
    },
  })

  const isPostPinningEnabled =
    featureFlagData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === POST_PINNING_FEATURE_FLAG
    )?.enabled ?? false

  const isImpressionTrackingEnabled =
    featureFlagData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === POST_IMPRESSION_EVENTS_FEATURE_FLAG
    )?.enabled ?? false

  const postListContent = (
    <Box flexDirection="column" gap="s16">
      {firstPostId ? (
        <Box key={firstPostId} {...postContainerStyles}>
          <CommunityPost
            id={firstPostId}
            isPostPinningEnabled={isPostPinningEnabled}
            {...callbacks}
          />
        </Box>
      ) : (
        <>{loadingPosts && <Skeleton />}</>
      )}
      {showKudosCard && ((isFromCommunities && posts.length > 0) || !isFromCommunities) && (
        <HighlightBanner {...kudosBannerProps} />
      )}
      {posts.slice(1).map((postId, index) => (
        <Box key={postId} {...postContainerStyles}>
          <CommunityPost
            id={postId}
            isPostPinningEnabled={isPostPinningEnabled}
            {...callbacks}
            onVisible={index + 2 === posts.length ? getNextPage : undefined}
          />
        </Box>
      ))}
      {!posts.length && !loadingPosts && (
        <Box className={'CommunityPostList_emptyStateWrapper'}>
          <WidgetEmptyState
            title={i18n.t('new_dashboard.posts.empty.title')}
            description={i18n.t('new_dashboard.posts.empty.description')}
            emoji="📰"
            actions={
              canCreatePosts
                ? [
                    {
                      label: i18n.t('new_dashboard.posts.write_post'),
                      icon: Pencil,
                      onClick: handleClickCreatePost,
                      variant: 'outline',
                    },
                  ]
                : undefined
            }
          />
        </Box>
      )}
      {loadingNextPosts && <Skeleton />}
    </Box>
  )

  return (
    <PostImpressionTrackerProvider enabled={isImpressionTrackingEnabled}>
      {insightsPostId && (
        <PostInsightsModal
          postId={insightsPostId}
          isOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setInsightsPostId(null)
            }
          }}
        />
      )}
      {postListContent}
    </PostImpressionTrackerProvider>
  )
}

export default memo(CommunityPostList)
